/**
 * Billing Page
 *
 * Displays credit packages with PayPal payment integration.
 * - 4 credit packages: 35/$3.99, 100/$9.99, 500/$39.99, 1000/$69.99
 * - Limited Time Special badge (green) for 35 credits
 * - BEST VALUE badge (gold) for 500 credits
 * - PayPal button for each package
 * - Real-time credit balance display
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - NO localStorage
 * - Sandbox mode ONLY for Phase 10
 * - Greens/blues color palette
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { adminFunctions } from '@/firebaseConfig';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { useToast } from '@/contexts/ToastContext';
import { Button, Card, LoadingSkeleton } from '@/components/ui';
import type { CreditPackage } from '@/shared/types';

/**
 * Credit packages as defined in spec 3.7.2
 */
const CREDIT_PACKAGES: CreditPackage[] = [
  {
    id: 'limited-special',
    name: 'Limited Time Special',
    credits: 35,
    priceCAD: 3.99,
    badge: 'LIMITED TIME SPECIAL',
    highlighted: true,
    description: 'Perfect for trying out our AI services',
  },
  {
    id: 'starter',
    name: 'Starter',
    credits: 100,
    priceCAD: 9.99,
    description: 'Great for individual agents',
  },
  {
    id: 'popular',
    name: 'Popular',
    credits: 500,
    priceCAD: 39.99,
    badge: 'BEST VALUE',
    highlighted: true,
    description: 'Most popular choice for professionals',
  },
  {
    id: 'pro',
    name: 'Pro',
    credits: 1000,
    priceCAD: 69.99,
    description: 'For high-volume users',
  },
];

export function Billing(): React.JSX.Element {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { credits, loading: loadingCredits } = useCredits();
  const { showToast } = useToast();

  const [processingPackageId, setProcessingPackageId] = useState<string | null>(null);

  /**
   * Calculate per-credit cost for display
   */
  const getPerCreditCost = (priceCAD: number, credits: number): string => {
    const perCredit = priceCAD / credits;
    return perCredit.toFixed(3);
  };

  /**
   * Create PayPal order (server-side)
   */
  const createOrder = async (packageId: string): Promise<string> => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    setProcessingPackageId(packageId);

    try {
      // Call createPayPalOrder Cloud Function (in us-central1)
      const createPayPalOrderFunc = httpsCallable(adminFunctions, 'createPayPalOrder');

      const result = await createPayPalOrderFunc({ packageId });
      const { paypalOrderId } = result.data as {
        paypalOrderId: string;
        transactionId: string;
      };

      console.log('PayPal order created:', paypalOrderId);
      return paypalOrderId;
    } catch (error) {
      console.error('Create order error:', error);
      setProcessingPackageId(null);
      throw error;
    }
  };

  /**
   * Handle PayPal payment approval
   */
  const onApprove = async (): Promise<void> => {
    try {
      // PayPal webhook will handle credit fulfillment
      // Just show success message and redirect
      showToast('Payment successful! Credits will be added shortly.', 'success');

      // Wait 2 seconds for webhook to process
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Approve error:', error);
      showToast('Payment processed, but there was an error. Please contact support.', 'error');
    } finally {
      setProcessingPackageId(null);
    }
  };

  /**
   * Handle PayPal payment error
   */
  const onError = (err: Record<string, unknown>): void => {
    console.error('PayPal error:', err);
    showToast('Payment failed. Please try again.', 'error');
    setProcessingPackageId(null);
  };

  /**
   * Handle PayPal payment cancellation
   */
  const onCancel = (): void => {
    showToast('Payment cancelled', 'info');
    setProcessingPackageId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Buy Credits</h1>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Current Balance</p>
                {loadingCredits ? (
                  <LoadingSkeleton variant="text" width="w-16" height="h-6" />
                ) : (
                  <p className="text-2xl font-bold text-green-600">{credits}</p>
                )}
              </div>
              <Button variant="secondary" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Info Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Credit Package
          </h2>
          <p className="text-lg text-gray-600 mb-2">
            Pay once, use anytime. Credits never expire.
          </p>
          <p className="text-md text-gray-500">
            Image services: 5 credits • Property descriptions: 2 credits
          </p>
          <div className="mt-4 bg-blue-50 border border-blue-200 text-blue-700 px-6 py-3 rounded-lg inline-block">
            <p className="font-semibold">🎉 FREE Retries on All Services!</p>
            <p className="text-sm">Pay once for an order, retry multiple times for free</p>
          </div>
        </div>

        {/* Credit Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {CREDIT_PACKAGES.map((pkg) => {
            const isProcessing = processingPackageId === pkg.id;

            return (
              <Card
                key={pkg.id}
                className={`relative transition-all duration-200 hover:shadow-2xl ${
                  pkg.highlighted
                    ? 'ring-2 ring-blue-500 transform hover:scale-105'
                    : 'hover:shadow-xl'
                }`}
              >
                {/* Badge */}
                {pkg.badge && (
                  <div
                    className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold shadow-lg ${
                      pkg.badge === 'LIMITED TIME SPECIAL'
                        ? 'bg-green-500 text-white'
                        : 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
                    }`}
                  >
                    {pkg.badge}
                  </div>
                )}

                <div className="p-6 text-center">
                  {/* Package Name */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>

                  {/* Credits */}
                  <div className="mb-4">
                    <p className="text-5xl font-bold text-blue-600">{pkg.credits}</p>
                    <p className="text-sm text-gray-600">credits</p>
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <p className="text-3xl font-bold text-gray-900">
                      ${pkg.priceCAD.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500">
                      ≈ ${getPerCreditCost(pkg.priceCAD, pkg.credits)} per credit
                    </p>
                  </div>

                  {/* Description */}
                  {pkg.description && (
                    <p className="text-sm text-gray-600 mb-6">{pkg.description}</p>
                  )}

                  {/* PayPal Button */}
                  <div className="mt-6">
                    {isProcessing ? (
                      <div className="flex items-center justify-center py-3">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-600">Processing...</span>
                      </div>
                    ) : (
                      <PayPalButtons
                        createOrder={async () => await createOrder(pkg.id)}
                        onApprove={async () => await onApprove()}
                        onError={onError}
                        onCancel={onCancel}
                        style={{
                          layout: 'vertical',
                          color: 'gold',
                          shape: 'rect',
                          label: 'paypal',
                        }}
                      />
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <Card className="bg-gradient-to-r from-blue-50 to-green-50">
          <div className="p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              Why Choose List8.app?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <span className="text-4xl mb-2 block">💳</span>
                <h4 className="font-semibold text-gray-900 mb-2">Credits Never Expire</h4>
                <p className="text-sm text-gray-600">
                  Buy once, use anytime. No monthly subscriptions or hidden fees.
                </p>
              </div>
              <div className="text-center">
                <span className="text-4xl mb-2 block">🔄</span>
                <h4 className="font-semibold text-gray-900 mb-2">FREE Retries</h4>
                <p className="text-sm text-gray-600">
                  Not satisfied? Retry multiple times at no extra cost.
                </p>
              </div>
              <div className="text-center">
                <span className="text-4xl mb-2 block">🚀</span>
                <h4 className="font-semibold text-gray-900 mb-2">Lightning Fast</h4>
                <p className="text-sm text-gray-600">
                  Powered by cutting-edge AI. Get results in seconds, not hours.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* FAQ Section */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Have Questions?
          </h3>
          <p className="text-gray-600 mb-6">
            Check out our <a href="/faq" className="text-blue-600 hover:underline">FAQ</a> or{' '}
            <a href="/support" className="text-blue-600 hover:underline">contact support</a>.
          </p>
        </div>
      </main>
    </div>
  );
}
